import { LocalizedLink } from "./LocalizedLink";
import { useLanguage } from "@/hooks/useLanguage";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ArrowRight } from "lucide-react";

interface RelatedLink {
  title: string;
  description: string;
  path: string;
}

interface RelatedLinksProps {
  category: "immigration" | "criminal" | "family" | "personalInjury" | "realEstate" | "business" | "probate" | "tax";
}

const relatedLinksData: Record<string, Record<string, RelatedLink[]>> = {
  immigration: {
    en: [
      {
        title: "Family-Based Immigration",
        description: "Reunite with your loved ones through family petitions",
        path: "/practice-areas/immigration-services/family-based-immigration"
      },
      {
        title: "Citizenship & Naturalization",
        description: "Become a U.S. citizen with comprehensive guidance",
        path: "/practice-areas/immigration-services/citizenship-naturalization"
      },
      {
        title: "Deportation Defense",
        description: "Protect your right to stay in the United States",
        path: "/practice-areas/immigration-services/deportation-removal-defense"
      },
      {
        title: "Asylum Services",
        description: "Seek protection and safety in the United States",
        path: "/practice-areas/immigration-services/asylum-in-the-united-states"
      }
    ],
    es: [
      {
        title: "Inmigración Familiar",
        description: "Reúnase con sus seres queridos a través de peticiones familiares",
        path: "/practice-areas/immigration-services/family-based-immigration"
      },
      {
        title: "Ciudadanía y Naturalización",
        description: "Conviértase en ciudadano estadounidense con nuestra guía integral",
        path: "/practice-areas/immigration-services/citizenship-naturalization"
      },
      {
        title: "Defensa de Deportación",
        description: "Proteja su derecho a permanecer en los Estados Unidos",
        path: "/practice-areas/immigration-services/deportation-removal-defense"
      },
      {
        title: "Servicios de Asilo",
        description: "Busque protección y seguridad en los Estados Unidos",
        path: "/practice-areas/immigration-services/asylum-in-the-united-states"
      }
    ],
    vi: [
      {
        title: "Di Trú Gia Đình",
        description: "Đoàn tụ với người thân qua đơn bảo lãnh gia đình",
        path: "/practice-areas/immigration-services/family-based-immigration"
      },
      {
        title: "Quốc Tịch & Nhập Tịch",
        description: "Trở thành công dân Mỹ với sự hướng dẫn chuyên nghiệp",
        path: "/practice-areas/immigration-services/citizenship-naturalization"
      },
      {
        title: "Bảo Vệ Truy Xuất",
        description: "Bảo vệ quyền ở lại Hoa Kỳ của bạn",
        path: "/practice-areas/immigration-services/deportation-removal-defense"
      },
      {
        title: "Dịch Vụ Ti Nạn",
        description: "Tìm kiếm sự bảo vệ và an toàn tại Hoa Kỳ",
        path: "/practice-areas/immigration-services/asylum-in-the-united-states"
      }
    ]
  },
  criminal: {
    en: [
      {
        title: "DWI Defense",
        description: "Skilled defense for DWI and DUI charges",
        path: "/practice-areas/criminal-defense/dwi"
      },
      {
        title: "Drug Charges",
        description: "Aggressive defense for drug-related offenses",
        path: "/practice-areas/criminal-defense/drug-charges"
      },
      {
        title: "Assault & Family Violence",
        description: "Protect your rights in assault cases",
        path: "/practice-areas/criminal-defense/assault-family-violence"
      },
      {
        title: "Expunction & Nondisclosure",
        description: "Clear your criminal record and start fresh",
        path: "/practice-areas/criminal-defense/expunction-nondisclosure"
      }
    ],
    es: [
      {
        title: "Defensa DWI",
        description: "Defensa calificada para cargos de DWI y DUI",
        path: "/practice-areas/criminal-defense/dwi"
      },
      {
        title: "Cargos de Drogas",
        description: "Defensa agresiva para delitos relacionados con drogas",
        path: "/practice-areas/criminal-defense/drug-charges"
      },
      {
        title: "Asalto y Violencia Familiar",
        description: "Proteja sus derechos en casos de asalto",
        path: "/practice-areas/criminal-defense/assault-family-violence"
      },
      {
        title: "Expunción y No Divulgación",
        description: "Limpie su historial criminal y comience de nuevo",
        path: "/practice-areas/criminal-defense/expunction-nondisclosure"
      }
    ],
    vi: [
      {
        title: "Bảo Vệ DWI",
        description: "Bảo vệ chuyên nghiệp cho các cáo buộc DWI và DUI",
        path: "/practice-areas/criminal-defense/dwi"
      },
      {
        title: "Cáo Buộc Ma Túy",
        description: "Bảo vệ mạnh mẽ cho các tội phạm liên quan đến ma túy",
        path: "/practice-areas/criminal-defense/drug-charges"
      },
      {
        title: "Hành Hung & Bạo Lực Gia Đình",
        description: "Bảo vệ quyền của bạn trong các vụ hành hung",
        path: "/practice-areas/criminal-defense/assault-family-violence"
      },
      {
        title: "Xóa Án & Không Tiết Lộ",
        description: "Xóa sạch hồ sơ hình sự và bắt đầu lại",
        path: "/practice-areas/criminal-defense/expunction-nondisclosure"
      }
    ]
  },
  family: {
    en: [
      {
        title: "Divorce",
        description: "Compassionate guidance through the divorce process",
        path: "/practice-areas/family-law/divorce"
      },
      {
        title: "Child Custody",
        description: "Protect your parental rights and child's best interests",
        path: "/practice-areas/family-law/child-custody"
      },
      {
        title: "Child Support",
        description: "Ensure fair child support arrangements",
        path: "/practice-areas/family-law/child-support"
      },
      {
        title: "CPS Defense",
        description: "Defend your family against CPS investigations",
        path: "/practice-areas/family-law/cps-defense"
      }
    ],
    es: [
      {
        title: "Divorcio",
        description: "Orientación compasiva durante el proceso de divorcio",
        path: "/practice-areas/family-law/divorce"
      },
      {
        title: "Custodia de Menores",
        description: "Proteja sus derechos parentales y el interés superior del niño",
        path: "/practice-areas/family-law/child-custody"
      },
      {
        title: "Manutención de Menores",
        description: "Asegure acuerdos justos de manutención infantil",
        path: "/practice-areas/family-law/child-support"
      },
      {
        title: "Defensa CPS",
        description: "Defienda a su familia contra investigaciones de CPS",
        path: "/practice-areas/family-law/cps-defense"
      }
    ],
    vi: [
      {
        title: "Ly Hôn",
        description: "Hướng dẫn chu đáo qua quá trình ly hôn",
        path: "/practice-areas/family-law/divorce"
      },
      {
        title: "Quyền Nuôi Con",
        description: "Bảo vệ quyền làm cha mẹ và lợi ích tốt nhất của trẻ",
        path: "/practice-areas/family-law/child-custody"
      },
      {
        title: "Hỗ Trợ Trẻ Em",
        description: "Đảm bảo thỏa thuận hỗ trợ trẻ em công bằng",
        path: "/practice-areas/family-law/child-support"
      },
      {
        title: "Bảo Vệ CPS",
        description: "Bảo vệ gia đình bạn trước các cuộc điều tra CPS",
        path: "/practice-areas/family-law/cps-defense"
      }
    ]
  },
  personalInjury: {
    en: [
      {
        title: "Auto Accident",
        description: "Get compensation for your car accident injuries",
        path: "/practice-areas/personal-injury/auto-accident"
      },
      {
        title: "Truck Accident",
        description: "Specialized representation for truck accident victims",
        path: "/practice-areas/personal-injury/truck-accident"
      },
      {
        title: "Medical Malpractice",
        description: "Hold negligent healthcare providers accountable",
        path: "/practice-areas/personal-injury/medical-malpractice"
      },
      {
        title: "Industrial Accident",
        description: "Protect your rights after a workplace injury",
        path: "/practice-areas/personal-injury/industrial-accident"
      }
    ],
    es: [
      {
        title: "Accidente de Auto",
        description: "Obtenga compensación por sus lesiones en accidente de auto",
        path: "/practice-areas/personal-injury/auto-accident"
      },
      {
        title: "Accidente de Camión",
        description: "Representación especializada para víctimas de accidentes de camión",
        path: "/practice-areas/personal-injury/truck-accident"
      },
      {
        title: "Negligencia Médica",
        description: "Responsabilice a los proveedores de salud negligentes",
        path: "/practice-areas/personal-injury/medical-malpractice"
      },
      {
        title: "Accidente Industrial",
        description: "Proteja sus derechos después de una lesión laboral",
        path: "/practice-areas/personal-injury/industrial-accident"
      }
    ],
    vi: [
      {
        title: "Tai Nạn Ô Tô",
        description: "Nhận bồi thường cho chấn thương tai nạn xe hơi của bạn",
        path: "/practice-areas/personal-injury/auto-accident"
      },
      {
        title: "Tai Nạn Xe Tải",
        description: "Đại diện chuyên biệt cho nạn nhân tai nạn xe tải",
        path: "/practice-areas/personal-injury/truck-accident"
      },
      {
        title: "Sai Sót Y Khoa",
        description: "Buộc các nhà cung cấp y tế sơ suất phải chịu trách nhiệm",
        path: "/practice-areas/personal-injury/medical-malpractice"
      },
      {
        title: "Tai Nạn Công Nghiệp",
        description: "Bảo vệ quyền của bạn sau chấn thương nơi làm việc",
        path: "/practice-areas/personal-injury/industrial-accident"
      }
    ]
  },
  realEstate: {
    en: [
      {
        title: "Real Estate Closings",
        description: "Smooth and secure property transactions",
        path: "/practice-areas/real-estate-law/closings"
      },
      {
        title: "Buying & Selling",
        description: "Comprehensive guidance for property purchases and sales",
        path: "/practice-areas/real-estate-law/buying-selling"
      },
      {
        title: "Foreclosures & Evictions",
        description: "Protect your property rights",
        path: "/practice-areas/real-estate-law/foreclosures-evictions"
      },
      {
        title: "Title Services",
        description: "Comprehensive title search and insurance",
        path: "/practice-areas/real-estate-law/title-services"
      }
    ],
    es: [
      {
        title: "Cierres Inmobiliarios",
        description: "Transacciones de propiedad seguras y fluidas",
        path: "/practice-areas/real-estate-law/closings"
      },
      {
        title: "Compra y Venta",
        description: "Orientación integral para compras y ventas de propiedades",
        path: "/practice-areas/real-estate-law/buying-selling"
      },
      {
        title: "Ejecuciones y Desalojos",
        description: "Proteja sus derechos de propiedad",
        path: "/practice-areas/real-estate-law/foreclosures-evictions"
      },
      {
        title: "Servicios de Título",
        description: "Búsqueda exhaustiva de títulos y seguros",
        path: "/practice-areas/real-estate-law/title-services"
      }
    ],
    vi: [
      {
        title: "Đóng Giao Dịch Bất Động Sản",
        description: "Giao dịch bất động sản trơn tru và an toàn",
        path: "/practice-areas/real-estate-law/closings"
      },
      {
        title: "Mua & Bán",
        description: "Hướng dẫn chuyên nghiệp cho mua bán bất động sản",
        path: "/practice-areas/real-estate-law/buying-selling"
      },
      {
        title: "Tịch Thu & Trục Xuất",
        description: "Bảo vệ quyền sở hữu của bạn",
        path: "/practice-areas/real-estate-law/foreclosures-evictions"
      },
      {
        title: "Dịch Vụ Chứng Thư",
        description: "Tìm kiếm và bảo hiểm chứng thư toàn diện",
        path: "/practice-areas/real-estate-law/title-services"
      }
    ]
  },
  business: {
    en: [
      {
        title: "Business Formation",
        description: "Start your business the right way",
        path: "/practice-areas/business-law/business-formation"
      },
      {
        title: "Contracts & Agreements",
        description: "Protect your business with solid contracts",
        path: "/practice-areas/business-law/contracts-agreements"
      },
      {
        title: "Purchase & Sale of Business",
        description: "Navigate business acquisitions and sales",
        path: "/practice-areas/business-law/purchase-sale-business"
      },
      {
        title: "Debt Collection",
        description: "Recover outstanding business debts",
        path: "/practice-areas/business-law/debt-collection"
      }
    ],
    es: [
      {
        title: "Formación de Negocios",
        description: "Inicie su negocio de la manera correcta",
        path: "/practice-areas/business-law/business-formation"
      },
      {
        title: "Contratos y Acuerdos",
        description: "Proteja su negocio con contratos sólidos",
        path: "/practice-areas/business-law/contracts-agreements"
      },
      {
        title: "Compra y Venta de Negocios",
        description: "Navegue adquisiciones y ventas de negocios",
        path: "/practice-areas/business-law/purchase-sale-business"
      },
      {
        title: "Cobro de Deudas",
        description: "Recupere deudas comerciales pendientes",
        path: "/practice-areas/business-law/debt-collection"
      }
    ],
    vi: [
      {
        title: "Thành Lập Doanh Nghiệp",
        description: "Bắt đầu doanh nghiệp đúng cách",
        path: "/practice-areas/business-law/business-formation"
      },
      {
        title: "Hợp Đồng & Thỏa Thuận",
        description: "Bảo vệ doanh nghiệp với hợp đồng vững chắc",
        path: "/practice-areas/business-law/contracts-agreements"
      },
      {
        title: "Mua & Bán Doanh Nghiệp",
        description: "Điều hướng việc mua bán doanh nghiệp",
        path: "/practice-areas/business-law/purchase-sale-business"
      },
      {
        title: "Thu Hồi Nợ",
        description: "Thu hồi các khoản nợ doanh nghiệp tồn đọng",
        path: "/practice-areas/business-law/debt-collection"
      }
    ]
  },
  probate: {
    en: [
      {
        title: "Estate Planning",
        description: "Secure your family's financial future",
        path: "/practice-areas/probate-estate/estate-planning"
      },
      {
        title: "Probate Administration",
        description: "Navigate the probate process efficiently",
        path: "/practice-areas/probate-estate/probate-administration"
      },
      {
        title: "Guardianship",
        description: "Protect vulnerable family members",
        path: "/practice-areas/probate-estate/guardianship"
      },
      {
        title: "Special Needs Trust",
        description: "Ensure care for loved ones with special needs",
        path: "/practice-areas/probate-estate/special-needs-trust"
      }
    ],
    es: [
      {
        title: "Planificación Patrimonial",
        description: "Asegure el futuro financiero de su familia",
        path: "/practice-areas/probate-estate/estate-planning"
      },
      {
        title: "Administración de Sucesiones",
        description: "Navegue el proceso de sucesión eficientemente",
        path: "/practice-areas/probate-estate/probate-administration"
      },
      {
        title: "Tutela",
        description: "Proteja a miembros vulnerables de la familia",
        path: "/practice-areas/probate-estate/guardianship"
      },
      {
        title: "Fideicomiso de Necesidades Especiales",
        description: "Asegure el cuidado de seres queridos con necesidades especiales",
        path: "/practice-areas/probate-estate/special-needs-trust"
      }
    ],
    vi: [
      {
        title: "Lập Kế Hoạch Di Sản",
        description: "Bảo đảm tương lai tài chính cho gia đình",
        path: "/practice-areas/probate-estate/estate-planning"
      },
      {
        title: "Quản Lý Di Chúc",
        description: "Điều hướng quy trình di chúc hiệu quả",
        path: "/practice-areas/probate-estate/probate-administration"
      },
      {
        title: "Giám Hộ",
        description: "Bảo vệ các thành viên gia đình dễ bị tổn thương",
        path: "/practice-areas/probate-estate/guardianship"
      },
      {
        title: "Ủy Thác Nhu Cầu Đặc Biệt",
        description: "Đảm bảo chăm sóc cho người thân có nhu cầu đặc biệt",
        path: "/practice-areas/probate-estate/special-needs-trust"
      }
    ]
  },
  tax: {
    en: [
      {
        title: "Tax Planning",
        description: "Strategic tax planning for individuals and businesses",
        path: "/practice-areas/tax-law/tax-planning"
      },
      {
        title: "Tax Debt Resolution",
        description: "Resolve your tax debt problems",
        path: "/practice-areas/tax-law/tax-debt-resolution"
      },
      {
        title: "Tax Litigation",
        description: "Skilled representation in tax disputes",
        path: "/practice-areas/tax-law/tax-litigation"
      },
      {
        title: "Business Tax Law",
        description: "Navigate complex business tax issues",
        path: "/practice-areas/business-law"
      }
    ],
    es: [
      {
        title: "Planificación Fiscal",
        description: "Planificación fiscal estratégica para individuos y empresas",
        path: "/practice-areas/tax-law/tax-planning"
      },
      {
        title: "Resolución de Deuda Fiscal",
        description: "Resuelva sus problemas de deuda fiscal",
        path: "/practice-areas/tax-law/tax-debt-resolution"
      },
      {
        title: "Litigio Fiscal",
        description: "Representación calificada en disputas fiscales",
        path: "/practice-areas/tax-law/tax-litigation"
      },
      {
        title: "Derecho Fiscal Empresarial",
        description: "Navegue problemas fiscales empresariales complejos",
        path: "/practice-areas/business-law"
      }
    ],
    vi: [
      {
        title: "Lập Kế Hoạch Thuế",
        description: "Lập kế hoạch thuế chiến lược cho cá nhân và doanh nghiệp",
        path: "/practice-areas/tax-law/tax-planning"
      },
      {
        title: "Giải Quyết Nợ Thuế",
        description: "Giải quyết các vấn đề nợ thuế của bạn",
        path: "/practice-areas/tax-law/tax-debt-resolution"
      },
      {
        title: "Kiện Tụng Thuế",
        description: "Đại diện chuyên nghiệp trong các tranh chấp thuế",
        path: "/practice-areas/tax-law/tax-litigation"
      },
      {
        title: "Luật Thuế Doanh Nghiệp",
        description: "Điều hướng các vấn đề thuế doanh nghiệp phức tạp",
        path: "/practice-areas/business-law"
      }
    ]
  }
};

export const RelatedLinks = ({ category }: RelatedLinksProps) => {
  const { language } = useLanguage();
  const links = relatedLinksData[category][language];

  const titles = {
    en: "Related Services",
    es: "Servicios Relacionados",
    vi: "Dịch Vụ Liên Quan"
  };

  return (
    <section className="py-12 bg-muted/30">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold mb-8 text-center">{titles[language]}</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {links.map((link, index) => (
            <LocalizedLink key={index} to={link.path}>
              <Card className="h-full hover:shadow-lg transition-shadow group">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-lg group-hover:text-primary transition-colors">
                    {link.title}
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{link.description}</p>
                </CardContent>
              </Card>
            </LocalizedLink>
          ))}
        </div>
      </div>
    </section>
  );
};
